import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';
import { MailWarning, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/apiError';

/**
 * Shows a banner when the current user's email isn't verified,
 * with a one-click "Resend verification email" action.
 *
 * Renders nothing when the user is verified or not loaded yet.
 */
export const EmailVerificationBanner = ({ message, className }) => {
  const { user, api } = useAuth();
  const [sending, setSending] = useState(false);

  if (!user || user.email_verified) return null;

  const handleResend = async () => {
    setSending(true);
    try {
      await api.post('/auth/resend-verification');
      toast.success('Verification email sent — please check your inbox.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not send verification email.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Alert variant="warning" className={`border-amber-500 bg-amber-500/10 ${className || ''}`} data-testid="email-verification-banner">
      <MailWarning className="h-4 w-4 text-amber-700" />
      <AlertTitle className="text-amber-800">Verify your email to send invoices</AlertTitle>
      <AlertDescription className="text-amber-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <span>
          {message || 'For security, invoices, campaigns and payments unlock once you confirm your email address.'}
          {' '}Sent to <strong>{user.email}</strong>.{' '}
          <Link
            to="/welcome"
            className="underline underline-offset-2 hover:text-amber-900"
            data-testid="banner-wrong-email-link"
          >
            Wrong email?
          </Link>
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={handleResend}
          disabled={sending}
          className="shrink-0"
          data-testid="resend-verification-btn"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
          Resend verification email
        </Button>
      </AlertDescription>
    </Alert>
  );
};

export default EmailVerificationBanner;
